import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { endpoints } from '@/modules/messaging/constants';

import { messagingQueryKeys } from './queryKeys';

export const OUTREACH_THREADS_PAGE_SIZE = 25;

/** How many failed threads the attention section reads in one call. */
const ATTENTION_LIMIT = 100;

export interface OutreachThreadFilters {
  tenantId?: string;
  /** Candidate name, matched case-insensitively by the API. */
  search?: string;
  status?: 'Sent' | 'Pending' | 'Failed';
  jobId?: string;
  /** Inclusive lower bound on thread activity, ISO instant. */
  from?: string;
  /** Exclusive upper bound on thread activity, ISO instant. */
  to?: string;
}

/** Drops empty values so the query key and the request stay comparable. */
const toQuery = (filters: OutreachThreadFilters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => Boolean(value)),
  );

/**
 * The outreach log. Filters are applied by the API and bound into the
 * cursor, so changing one starts a new page sequence.
 *
 * `shouldFetch` is false until we know the user has a LinkedIn account:
 * without one there is nothing to page through, and the page asks them to
 * connect instead of showing an empty list.
 */
export function useOutreachThreadsQuery(
  filters: OutreachThreadFilters,
  shouldFetch: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: messagingQueryKeys.threadList(toQuery(filters)),
    initialPageParam: undefined as string | undefined,
    queryFn: shouldFetch
      ? async ({ pageParam }) => {
          const { data } = await fetchClient.GET(endpoints.threads, {
            params: {
              query: {
                ...toQuery(filters),
                cursor: pageParam,
                limit: OUTREACH_THREADS_PAGE_SIZE,
              },
            },
          });
          return data;
        }
      : skipToken,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    // A filter change keeps the current rows until the new ones land, rather
    // than flashing the skeleton on every click and keystroke.
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    threads: query.data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    totalCount: query.data?.pages[0]?.metadata.totalCount ?? 0,
  };
}

/** Failed threads for the selected tenant, independent of the log filters. */
export function useFailedOutreachThreadsQuery(
  tenantId: string | undefined,
  shouldFetch: boolean,
) {
  return useQuery({
    queryKey: messagingQueryKeys.threadList({
      tenantId,
      status: 'Failed',
      attention: 1,
    }),
    queryFn:
      shouldFetch && tenantId
        ? async () => {
            const { data } = await fetchClient.GET(endpoints.threads, {
              params: {
                query: { tenantId, status: 'Failed', limit: ATTENTION_LIMIT },
              },
            });
            return data?.items;
          }
        : skipToken,
  });
}
